import React, { useMemo, useRef, useState } from "react";
import { X, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MagneticButton } from "../MagneticButton";
import { postJSON, patchJSON } from "../../lib/api";

const ROLES = [
    { id: "dc", label: "Domain Controller" },
    { id: "dns", label: "DNS" },
    { id: "gateway", label: "Gateway / Router" },
    { id: "file_server", label: "File Server" },
    { id: "mail", label: "Mail" },
    { id: "web", label: "Web" },
    { id: "api", label: "API" },
    { id: "nas", label: "NAS" },
    { id: "vpn", label: "VPN" },
    { id: "monitoring", label: "Monitoring" },
    { id: "database", label: "Database" },
    { id: "backup", label: "Backup" },
    { id: "other", label: "Other" },
];

const CRITICALITIES = [
    { id: "low", label: "Low", tone: "border-white/[0.1] text-text-muted" },
    { id: "medium", label: "Medium", tone: "border-accent-cyan/30 text-accent-cyan bg-accent-cyan/[0.06]" },
    { id: "high", label: "High", tone: "border-accent-amber/40 text-accent-amber bg-accent-amber/[0.08]" },
    { id: "critical", label: "Critical", tone: "border-accent-red/40 text-accent-red bg-accent-red/[0.08]" },
];

function isValidIPv4(s) {
    if (!s) return false;
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(s);
    if (!m) return false;
    return [1, 2, 3, 4].every((i) => {
        const n = +m[i];
        return n >= 0 && n <= 255;
    });
}

function ipInCidr(ip, cidr) {
    if (!ip || !cidr) return true;
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/.exec(cidr);
    const i = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip);
    if (!m || !i) return true;
    const toInt = (a, b, c, d) => ((+a << 24) | (+b << 16) | (+c << 8) | +d) >>> 0;
    const prefix = +m[5];
    const mask = prefix === 0 ? 0 : ((~0 << (32 - prefix)) >>> 0);
    const net = toInt(m[1], m[2], m[3], m[4]) & mask;
    const target = toInt(i[1], i[2], i[3], i[4]) & mask;
    return net === target;
}

/** Isolated IP field with local state (same pattern as MacField — typing
 *  doesn't re-render the parent form, no controlled-input race). */
const IpField = React.forwardRef(({ initial = "" }, ref) => {
    const [value, setValue] = React.useState(initial);
    const valid = useMemo(() => isValidIPv4(value), [value]);
    const empty = !value || !value.trim();
    React.useImperativeHandle(ref, () => ({ getValue: () => value, isValid: () => valid }), [value, valid]);
    return (
        <>
            <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="10.10.0.10"
                className={`w-full px-3 py-2 rounded-md bg-elevated/60 border text-text-primary text-[13px] font-mono focus:outline-none ${
                    !empty && !valid
                        ? "border-accent-amber/40 focus:border-accent-amber/60"
                        : "border-white/[0.06] focus:border-accent-cyan/40"
                }`}
                data-testid="ip-field-ip"
            />
            {!empty && !valid && (
                <p className="mt-1 text-[10.5px] font-mono text-accent-amber" data-testid="ip-field-ip-error">
                    Not a valid IPv4 address
                </p>
            )}
        </>
    );
});
IpField.displayName = "IpField";

export const ImportantIPForm = ({ client, subnets, hardware, asset, onClose, onSaved }) => {
    const isEdit = Boolean(asset);
    const ipRef = useRef(null);
    const [form, setForm] = useState({
        name: asset?.name || "",
        role: asset?.role || "dc",
        criticality: asset?.criticality || "medium",
        subnet_id: asset?.subnet_id || "",
        hardware_id: asset?.hardware_id || "",
        service_port: asset?.service_port ?? "",
        protocol: asset?.protocol || "tcp",
        fqdn: asset?.fqdn || "",
        notes: asset?.notes || "",
        tags: (asset?.tags || []).join(", "),
        status: asset?.status || "active",
    });
    const [expanded, setExpanded] = useState({ service: false, notes: false });
    const [saving, setSaving] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const linkedSubnet = useMemo(
        () => subnets.find((s) => s.id === form.subnet_id) || null,
        [subnets, form.subnet_id],
    );
    const linkedHardware = useMemo(
        () => hardware.find((h) => h.id === form.hardware_id) || null,
        [hardware, form.hardware_id],
    );

    // Live warning if hardware has a different primary IP than what we're entering
    const ipNow = ipRef.current?.getValue();
    const hardwareIpMismatch = linkedHardware && ipNow && linkedHardware.ip_address && linkedHardware.ip_address !== ipNow;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        const ipVal = (ipRef.current?.getValue() || "").trim();
        if (!ipVal || !isValidIPv4(ipVal)) { toast.error("Valid IPv4 required"); return; }
        if (linkedSubnet && !ipInCidr(ipVal, linkedSubnet.cidr)) {
            toast.error(`IP ${ipVal} is outside ${linkedSubnet.cidr}`); return;
        }
        const portNum = form.service_port === "" ? null : parseInt(form.service_port, 10);
        if (portNum !== null && (Number.isNaN(portNum) || portNum < 1 || portNum > 65535)) {
            toast.error("Port must be 1..65535"); return;
        }

        const payload = {
            name: form.name.trim(),
            ip_address: ipVal,
            role: form.role,
            criticality: form.criticality,
            subnet_id: form.subnet_id || null,
            hardware_id: form.hardware_id || null,
            service_port: portNum,
            protocol: form.protocol,
            fqdn: form.fqdn.trim() || null,
            notes: form.notes || null,
            tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
            status: form.status,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await patchJSON(`/ipam/important-ips/${asset.id}`, payload);
                toast.success("Important IP updated");
            } else {
                await postJSON(`/ipam/clients/${client.id}/important-ips`, payload);
                toast.success("Important IP added");
            }
            onSaved();
        } catch (err) {
            if (err.status === 422 && err.payload?.detail) {
                toast.error(typeof err.payload.detail === "string" ? err.payload.detail : "Validation failed");
            } else {
                toast.error(err.message || "Save failed");
            }
            setSaving(false);
        }
    };

    const Section = ({ id, label, children }) => (
        <div className="rounded-lg border border-white/[0.05] bg-elevated/30 overflow-hidden">
            <button
                type="button"
                onClick={() => setExpanded((s) => ({ ...s, [id]: !s[id] }))}
                className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-mono uppercase tracking-[0.18em] text-text-muted hover:text-text-primary transition-colors"
                data-testid={`ip-section-${id}`}
            >
                <span>{label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded[id] ? "rotate-180" : ""}`} />
            </button>
            {expanded[id] && <div className="px-3 pb-3 grid gap-3 md:grid-cols-2">{children}</div>}
        </div>
    );

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-canvas/80 backdrop-blur-md"
            data-testid="ip-form-modal"
            role="dialog"
            aria-modal="true"
        >
            <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 cursor-default" />
            <form
                onSubmit={submit}
                className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/[0.08] bg-surface/95 shadow-heavy-popover p-6 md:p-8"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-text-dim hover:text-text-primary p-1.5 rounded"
                    aria-label="Close"
                    data-testid="ip-form-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                    {isEdit ? "Edit important IP" : "Add important IP"}
                </p>
                <h2 className="mt-2 font-display font-semibold text-2xl text-text-primary tracking-tight">
                    {isEdit ? asset.name : "Track a critical host"}
                </h2>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                    <Field label="Name *">
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            placeholder="Domain Controller HQ"
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="ip-field-name"
                        />
                    </Field>
                    <Field label="IPv4 address *">
                        <IpField ref={ipRef} initial={asset?.ip_address || ""} />
                    </Field>
                    <Field label="Role *">
                        <select
                            value={form.role}
                            onChange={(e) => update("role", e.target.value)}
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="ip-field-role"
                        >
                            {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Criticality *">
                        <div className="flex gap-1.5" data-testid="ip-field-criticality">
                            {CRITICALITIES.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => update("criticality", c.id)}
                                    className={`flex-1 px-2 py-1.5 rounded text-[11.5px] font-medium border transition-colors ${
                                        form.criticality === c.id ? c.tone : "border-white/[0.06] text-text-muted hover:border-accent-cyan/30"
                                    }`}
                                    data-testid={`ip-field-criticality-${c.id}`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <Field label="Linked subnet">
                        <select
                            value={form.subnet_id}
                            onChange={(e) => update("subnet_id", e.target.value)}
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="ip-field-subnet"
                        >
                            <option value="">— none —</option>
                            {subnets.map((s) => (
                                <option key={s.id} value={s.id}>{s.cidr} · {s.name}</option>
                            ))}
                        </select>
                        {linkedSubnet && (
                            <p className="mt-1 text-[10.5px] text-text-dim">
                                IP must be within <span className="font-mono">{linkedSubnet.cidr}</span>
                            </p>
                        )}
                    </Field>
                    <Field label="Linked hardware">
                        <select
                            value={form.hardware_id}
                            onChange={(e) => update("hardware_id", e.target.value)}
                            className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                            data-testid="ip-field-hardware"
                        >
                            <option value="">— none —</option>
                            {hardware.map((h) => (
                                <option key={h.id} value={h.id}>
                                    {h.vendor} {h.model}{h.hostname ? ` · ${h.hostname}` : ""}
                                </option>
                            ))}
                        </select>
                        {hardwareIpMismatch && (
                            <p className="mt-1 text-[10.5px] text-accent-amber">
                                Hardware primary IP is <span className="font-mono">{linkedHardware.ip_address}</span> — different from this IP. That's ok if the host has multiple interfaces.
                            </p>
                        )}
                    </Field>
                </div>

                <div className="mt-5 space-y-3">
                    <Section id="service" label="Service details">
                        <Field label="Port">
                            <input
                                type="number"
                                min="1"
                                max="65535"
                                value={form.service_port}
                                onChange={(e) => update("service_port", e.target.value)}
                                placeholder="443"
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] font-mono focus:outline-none focus:border-accent-cyan/40"
                                data-testid="ip-field-port"
                            />
                        </Field>
                        <Field label="Protocol">
                            <select
                                value={form.protocol}
                                onChange={(e) => update("protocol", e.target.value)}
                                className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                data-testid="ip-field-protocol"
                            >
                                <option value="tcp">TCP</option>
                                <option value="udp">UDP</option>
                                <option value="both">Both</option>
                            </select>
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="FQDN">
                                <input
                                    type="text"
                                    value={form.fqdn}
                                    onChange={(e) => update("fqdn", e.target.value)}
                                    placeholder="dc01.client.local"
                                    className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] font-mono focus:outline-none focus:border-accent-cyan/40"
                                    data-testid="ip-field-fqdn"
                                />
                            </Field>
                        </div>
                    </Section>

                    <Section id="notes" label="Notes & tags">
                        <div className="md:col-span-2">
                            <Field label="Tags (comma-separated)">
                                <input
                                    type="text"
                                    value={form.tags}
                                    onChange={(e) => update("tags", e.target.value)}
                                    placeholder="critical, production"
                                    className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[13px] focus:outline-none focus:border-accent-cyan/40"
                                    data-testid="ip-field-tags"
                                />
                            </Field>
                        </div>
                        <div className="md:col-span-2">
                            <Field label="Notes (markdown)">
                                <textarea
                                    rows={4}
                                    value={form.notes}
                                    onChange={(e) => update("notes", e.target.value)}
                                    placeholder="Maintenance windows, contracts, escalation contacts…"
                                    className="w-full px-3 py-2 rounded-md bg-elevated/60 border border-white/[0.06] text-text-primary text-[12.5px] font-mono focus:outline-none focus:border-accent-cyan/40"
                                    data-testid="ip-field-notes"
                                />
                            </Field>
                        </div>
                    </Section>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border border-white/[0.08] text-[13px] text-text-muted hover:text-text-primary"
                        data-testid="ip-form-cancel"
                    >
                        Cancel
                    </button>
                    <MagneticButton
                        type="submit"
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                        ariaLabel="Save important IP"
                        data-testid="ip-form-submit"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        {saving ? "Saving…" : (isEdit ? "Save changes" : "Add IP")}
                    </MagneticButton>
                </div>
            </form>
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

export default ImportantIPForm;
